import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ContasRepetidas, Exportacao } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import {
  AgFilterUtils,
  AgGridPaginationStatusBar,
  buildGridOptions,
  exportGridAsExcel,
  Page,
  PAGE_DEFAULT,
  PaginationStatus,
} from '@gestor-web/app/shared/components';
import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { catchError, filter, first, takeUntil } from 'rxjs/operators';
import { ContasRepetidasService } from './contas-repetidas.service';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { BrlCurrencyPipe } from '@gestor-web/app/shared/pipes/brl-currency.pipe';
import { buildMenuExportacao, confirmarExportacaoAssincrona, isVisaoAgrupada, MOTIVO_BLOQUEIO_VISAO_AGRUPADA, tratarErroLimiteExportacao } from '@gestor-web/app/shared/utils/exportacao.utils';
import { ConfirmationService, MenuItem, SelectItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { GuiaMonitorActionCellRenderer } from '@gestor-web/app/pages/private/criticas/criticas-geradas/guia-monitor-action-cell.component';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.CONTAS_REPETIDAS_COM_DIFERENTES_CHAVES';

const FEATURE_KEY_EXPORTACAO = 'CONFORMIDADE.CONTAS_REPETIDAS_DIFERENTES_CHAVES.BOTAO-EXPORTAR_CSV';

const COLUMNS: ColDef[] = [
  {
    headerName: 'Cód. lancto',
    field: 'cd_guia_monitor',
    width: 120,
    cellRenderer: GuiaMonitorActionCellRenderer,
  },
  {
    headerName: '012-CNES executante',
    field: 'nr_cnes_exec',
    width: 130,
    cellClassRules: { 'cell-changed': p => p?.data?._changedFields?.includes('nr_cnes_exec') },
  },
  {
    headerName: '023-Nr. guia prestador',
    field: 'nr_guia_prestador',
    width: 150,
    cellClassRules: { 'cell-changed': p => p?.data?._changedFields?.includes('nr_guia_prestador') },
  },
  {
    headerName: '024-Nr. guia operadora',
    field: 'nr_guia_operadora',
    width: 140,
    cellClassRules: { 'cell-changed': p => p?.data?._changedFields?.includes('nr_guia_operadora') },
  },
  {
    headerName: '025-Nr. reembolso ou pagto eventual',
    field: 'nr_reembolso',
    width: 180,
  },
  {
    headerName: '013-Tipo de identificação - CPF/CNPJ',
    field: 'nm_id_cnpj_cpf_exec',
    width: 200,
  },
  {
    headerName: '014-CNPJ/CPF executante',
    field: 'nr_cnpj_cpf_exec',
    type: 'cpfCnpj',
    width: 150,
  },
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 140,
  },
  {
    headerName: 'Nr. lote',
    field: 'nr_lote',
    width: 140,
  },
  {
    headerName: '050-Valor informado da guia',
    field: 'vl_guia_informado',
    valueFormatter: params => new BrlCurrencyPipe().transform(params.value),
    width: 170,
  },
  {
    headerName: '058-Valor total glosa',
    field: 'vl_glosa',
    valueFormatter: params => new BrlCurrencyPipe().transform(params.value),
    width: 140,
  },
  {
    headerName: '059-Valor total pago',
    field: 'vl_pago',
    valueFormatter: params => new BrlCurrencyPipe().transform(params.value),
    width: 120,
  },
  {
    headerName: 'Situação na ANS',
    field: 'nm_st_guia_ans',
    width: 120,
  },
  {
    headerName: '029-Data realização ou início do atendimento',
    field: 'dt_realizacao',
    type: 'date',
    width: 200,
  },
];

@Component({
    templateUrl: './contas-repetidas.component.html',
    styleUrls: ['./contas-repetidas.component.scss'],
    providers: [ContasRepetidasService, DecimalPipe],
    standalone: false
})
export class ContasRepetidasComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;

  page: number = PAGE_DEFAULT.page;
  pageSize: number = PAGE_DEFAULT.limit;
  pageData: Page = PAGE_DEFAULT;
  paginationReset = false;
  pagination$: BehaviorSubject<PaginationStatus> = new BehaviorSubject<PaginationStatus>(null);

  gridOptions: GridOptions<Row>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  rowData: Row[];
  rowTotal: number = 0;

  dtUltimoProcessamento: string = null;

  selectedCondicao: string = null;
  selectedDataRealizacao: string[] = [];
  appliedCompetenciaRange: string[] = [];
  appliedSelectedCondicao: string = null;
  appliedSelectedDataRealizacao: string[] = [];

  condicaoOptions: SelectItem<string>[] = [];
  dataRealizacaoOptions: SelectItem<string>[] = [];

  exportMenuItems: MenuItem[] = [];

  detalheGuiaDisplay = false;
  detalheGuiaCodigo: string;

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  private hasAcessoExportacao = false;

  constructor(
    private service: ContasRepetidasService,
    private datePipe: DatePipe,
    private geralService: GeralService,
    private decimalPipe: DecimalPipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private competenciaPipe: CompetenciaPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private confirmationService: ConfirmationService,
    private permissaoService: PermissaoService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
    this.atualizarMenuExportacao();
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    this.condicaoOptions = [
      { label: 'CNES diferente', value: 'cnes' },
      { label: 'Nr. guia prestador diferente', value: 'nr_guia_prestador' },
      { label: 'Nr. guia operadora diferente', value: 'nr_guia_operadora' },
    ];

    this.dataRealizacaoOptions = [
      { label: 'Iguais', value: 'iguais' },
      { label: 'Diferentes', value: 'diferentes' },
    ];

    this.permissaoService
      .checkAcessoFeature(FEATURE_KEY_EXPORTACAO)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(hasAcesso => {
        this.hasAcessoExportacao = hasAcesso;
        this.atualizarMenuExportacao();
      });

    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([operadora, params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs: COLUMNS,
        pipes: {
          date: this.datePipe,
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        getRowClass: params => {
          const z = params?.data?._zebra ?? 0;
          return z === 1 ? 'group-alt-1' : 'group-alt-0';
        },
        context: {
          parentComponent: this,
        },
        statusBar: {
          statusPanels: [
            {
              statusPanel: AgGridPaginationStatusBar,
              statusPanelParams: { pagination$: this.pagination$ },
              align: 'left',
            },
          ],
        },
      },
    );
  }

  resetPagination() {
    this.page = 0;
    this.pageData = {
      ...this.pageData,
      page: 0,
      size: this.pageSize,
      offset: 0,
      limit: this.pageSize,
    };

    this.filterChanged = true;
  }

  private buildGroupKey(row: Row, condicao: string = this.selectedCondicao): string {
    switch (condicao) {
      case 'cnes':
        return [
          row.id_cnpj_cpf_exec,
          row.nr_cnpj_cpf_exec,
          row.nr_guia_prestador,
          row.nr_guia_operadora,
          row.nr_reembolso,
        ].join('|');
      case 'nr_guia_prestador':
        return [
          row.nr_cnes_exec,
          row.id_cnpj_cpf_exec,
          row.nr_cnpj_cpf_exec,
          row.nr_guia_operadora,
          row.nr_reembolso,
        ].join('|');
      case 'nr_guia_operadora':
        return [
          row.nr_cnes_exec,
          row.id_cnpj_cpf_exec,
          row.nr_cnpj_cpf_exec,
          row.nr_guia_prestador,
          row.nr_reembolso,
        ].join('|');
      default:
        return String(row.cd_guias_monitor);
    }
  }

  private getVaryingField(
    condicao: string = this.selectedCondicao,
  ): 'nr_cnes_exec' | 'nr_guia_prestador' | 'nr_guia_operadora' | null {
    switch (condicao) {
      case 'cnes':
        return 'nr_cnes_exec';
      case 'nr_guia_prestador':
        return 'nr_guia_prestador';
      case 'nr_guia_operadora':
        return 'nr_guia_operadora';
      default:
        return null;
    }
  }

  private valueKey(v: unknown): string {
    return v === null || v === undefined ? '__NULL__' : String(v);
  }

  private isDataRealizacaoFilledValue(values: string[]): boolean {
    return Array.isArray(values) && values.length > 0;
  }

  get isDataRealizacaoFilled(): boolean {
    return this.isDataRealizacaoFilledValue(this.selectedDataRealizacao);
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, condicao, data_realizacao, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (condicao) {
      hasFilter = true;
      this.selectedCondicao = condicao;
    }

    if (data_realizacao) {
      hasFilter = true;
      this.selectedDataRealizacao = Array.isArray(data_realizacao)
        ? (data_realizacao as string[])
        : String(data_realizacao)
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  private isDataRealizacaoAll(values: string[] = this.selectedDataRealizacao): boolean {
    const vals = this.dataRealizacaoOptions.map(o => o.value);
    return Array.isArray(values) && vals.every(v => values.includes(v)) && values.length === vals.length;
  }

  private getDataRealizacaoQuery(values: string[] = this.selectedDataRealizacao): string {
    return this.isDataRealizacaoAll(values) ? 'todos' : (values ?? []).join(',');
  }

  private snapshotAppliedFilters() {
    this.appliedCompetenciaRange = [...this.competenciaRange];
    this.appliedSelectedCondicao = this.selectedCondicao;
    this.appliedSelectedDataRealizacao = [...(this.selectedDataRealizacao ?? [])];
  }

  reloadData(applyPendingFilters: boolean = false) {
    const shouldApplyFilters = applyPendingFilters || !this.appliedCompetenciaRange?.length;
    if (shouldApplyFilters) {
      this.snapshotAppliedFilters();
      this.filterChanged = false;
    }

    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.isDataRealizacaoFilledValue(this.appliedSelectedDataRealizacao)) {
      this.agGrid?.api?.setGridOption('loading', false);
      this.notificationService.warn('Selecione "Data de realização" antes de pesquisar.');
      return;
    }

    if (!this.appliedCompetenciaRange[0] || !this.appliedCompetenciaRange[1]) {
      this.rowData = [];
      this.rowTotal = 0;
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      this.paginationReset = false;
      this.pagination$.next({ ...this.pageData, endOffset: this.rowTotal });
      return;
    }

    const dataRealizacao = this.getDataRealizacaoQuery(this.appliedSelectedDataRealizacao);

    this.pageData = {
      page: this.page,
      size: this.pageSize,
      offset: this.page * this.pageSize,
      limit: this.pageSize,
    };

    const grid$ = this.service.getDataGoApi(
      this.operadora,
      this.appliedCompetenciaRange[0],
      this.appliedCompetenciaRange[1],
      this.appliedSelectedCondicao,
      dataRealizacao,
      this.pageData,
    );

    const ultimo$ = this.service.getUltimoProcessamento(this.operadora).pipe(catchError(() => of(null)));

    const data$ = forkJoin({
      grid: grid$,
      ultimo: ultimo$,
    });

    data$.subscribe({
      next: ({ grid, ultimo }) => {
        const { items, total } = grid;

        this.rowTotal = total ?? 0;

        const dateResponse = (ultimo as any)?.items?.[0]?.dt_atualizacao_mon_guia;
        this.dtUltimoProcessamento = dateResponse ? this.datePipe.transform(+dateResponse, 'dd/MM/yyyy') : null;

        if (!items || !items.length) {
          this.rowData = [];
          this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
          this.paginationReset = false;
          this.pagination$.next({ ...this.pageData, endOffset: this.rowTotal });
          return;
        }

        let currentKey: string | null = null;
        let groupIndex = -1;

        this.rowData = items.map(item => {
          const row: Row = {
            ...item,
            nm_st_guia_ans: '',
            nm_id_cnpj_cpf_exec: '',
          };

          row.nm_st_guia_ans = this.geralService.buildNomeDeDominio(row.st_guia_ans, 'MON_GUIA_SITUACAO_ANS');
          row.nm_id_cnpj_cpf_exec = this.geralService.buildNomeDeDominio(row.id_cnpj_cpf_exec, 'MON_ID_CNPJ_CPF');

          return row;
        });

        this.rowData = this.rowData.map(r => {
          const key = this.buildGroupKey(r, this.appliedSelectedCondicao);
          if (key !== currentKey) {
            groupIndex++;
            currentKey = key;
          }
          return { ...r, _groupKey: key, _zebra: (groupIndex % 2) as 0 | 1 };
        });

        const varying = this.getVaryingField(this.appliedSelectedCondicao);

        if (varying) {
          const firstValueByGroup = new Map<string, string>();

          this.rowData = this.rowData.map(r => {
            const key = r._groupKey!;
            const current = this.valueKey((r as any)[varying]);
            const first = firstValueByGroup.get(key);

            let changedFields: string[] = [];

            if (first === undefined) {
              firstValueByGroup.set(key, current);
            } else {
              if (current !== first) {
                changedFields = [varying];
              }
            }

            return { ...r, _changedFields: changedFields };
          });
        } else {
          this.rowData = this.rowData.map(r => ({ ...r, _changedFields: [] }));
        }

        this.paginationReset = false;
        this.pagination$.next({ ...this.pageData, endOffset: this.rowTotal });
      },
      error: error => this.notificationService.handleError(error),
      complete: () => this.agGrid?.api?.setGridOption('loading', false),
    });
  }

  onFilterChange(): void {
    this.filterChanged = true;
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.resetPagination();
  }

  onPageChange(page: Page) {
    this.pageData = page;
    this.page = page.page;
    this.pageSize = page.size;

    this.reloadData();
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  showDetalheGuiaFor(cd_guia_monitor: string) {
    // console.log('showDetalheGuiaFor', cd_guia_monitor);
    this.detalheGuiaDisplay = true;
    this.detalheGuiaCodigo = cd_guia_monitor;
  }

  exportExcel(): void {
    const fileNamePrefix = `contas-repetidas-com-diferentes-chaves-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const dataRealizacaoTexto = this.dataRealizacaoOptions?.map(item => item.label).join(', ') ?? '';

    const headers = [
      'Contas repetidas com diferentes chaves',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
      'Condição: ' + this.condicaoOptions.find(opt => opt.value === this.selectedCondicao)?.label,
      'Data de realização: ' + dataRealizacaoTexto,
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  abrirMenuExportacao(event: Event, menu: Menu): void {
    this.atualizarMenuExportacao();
    menu.toggle(event);
  }

  private atualizarMenuExportacao(): void {
    this.exportMenuItems = buildMenuExportacao({
      onExportarLinhasRetornadas: () => this.exportExcel(),
      onExportarTodas: () => this.showConfirmationExportAll(),
      assincronaVisivel: this.hasAcessoExportacao,
      motivoBloqueioAssincrona: this.getMotivoBloqueioExportacaoAssincrona(),
    });
  }

  private getMotivoBloqueioExportacaoAssincrona(): string {
    return isVisaoAgrupada(this.agGrid?.api) ? MOTIVO_BLOQUEIO_VISAO_AGRUPADA : null;
  }

  private isExportacaoAssincronaBloqueada(): boolean {
    return !!this.getMotivoBloqueioExportacaoAssincrona();
  }

  showConfirmationExportAll(): void {
    if (this.isExportacaoAssincronaBloqueada()) {
      console.error('Exportação assíncrona indisponível no estado atual da tela');
      return;
    }

    confirmarExportacaoAssincrona({
      confirmationService: this.confirmationService,
      totalLinhas: this.rowTotal,
      totalColunas: this.agGrid.api.getAllDisplayedColumns().length,
      formatarNumero: valor => this.formatarNumero(valor),
      onConfirm: () => this.exportExcelRequest(),
    });
  }

  private formatarNumero(valor: number): string {
    return this.decimalPipe.transform(valor) ?? String(valor);
  }

  exportExcelRequest(): void {
    if (this.isExportacaoAssincronaBloqueada()) {
      console.error('Exportação assíncrona indisponível no estado atual da tela');
      return;
    }

    const cols = this.agGrid.api.getAllDisplayedColumns();
    const campos_order = cols.map(col => col.getColDef().field);
    const campos_map = cols.reduce(
      (m, col) => {
        m[col.getColDef().field] = col.getColDef().headerName;
        return m;
      },
      {} as Record<string, string>,
    );

    const params: Exportacao = {
      campos_order,
      campos_map,
      filtros: {
        competencia: this.appliedCompetenciaRange[0],
        competencia_final: this.appliedCompetenciaRange[1],
        condicao: this.appliedSelectedCondicao,
        data_realizacao: this.getDataRealizacaoQuery(this.appliedSelectedDataRealizacao),
      },
    };

    this.service.requestExportacao(this.operadora, params).subscribe({
      next: () =>
        this.notificationService.info(
          'O arquivo está sendo gerado. Você pode continuar usando o sistema normalmente.',
        ),
      error: err => {
        const bloqueado = tratarErroLimiteExportacao({
          confirmationService: this.confirmationService,
          erro: err,
          formatarNumero: valor => this.formatarNumero(valor),
        });
        if (!bloqueado) {
          this.notificationService.handleError(err);
        }
      },
    });
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const dataRealizacao = this.getDataRealizacaoQuery();

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      condicao: this.selectedCondicao,
      data_realizacao: dataRealizacao,
      page: this.page,
      pageSize: this.pageSize,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

interface Row extends ContasRepetidas {
  nm_st_guia_ans: string;
  nm_id_cnpj_cpf_exec: string;
  _groupKey?: string;
  _zebra?: 0 | 1;
}
